import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PushPin,
  Delete,
  Add,
  Visibility,
  VisibilityOff,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { useSnackbar } from 'notistack';
import { API_BASE_URL } from '../App';

const GalleryManagement = () => {
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [formData, setFormData] = useState({
    imageFile: null,
    prompt: '',
    aspect: 1.5,
    active: true,
    pinned: false,
  });
  const [showLikeCount, setShowLikeCount] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Load saved form data from localStorage
  useEffect(() => {
    const savedPrompt = localStorage.getItem('admin_prompt');
    const savedAspect = localStorage.getItem('admin_aspect');
    const savedActive = localStorage.getItem('admin_active');
    const savedPinned = localStorage.getItem('admin_pinned');

    if (savedPrompt) setFormData(prev => ({ ...prev, prompt: savedPrompt }));
    if (savedAspect) setFormData(prev => ({ ...prev, aspect: parseFloat(savedAspect) }));
    if (savedActive) setFormData(prev => ({ ...prev, active: savedActive === 'true' }));
    if (savedPinned) setFormData(prev => ({ ...prev, pinned: savedPinned === 'true' }));
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    localStorage.setItem('admin_prompt', formData.prompt);
    localStorage.setItem('admin_aspect', formData.aspect.toString());
    localStorage.setItem('admin_active', formData.active.toString());
    localStorage.setItem('admin_pinned', formData.pinned.toString());
  }, [formData]);

  const fetchGallery = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/gallery-list`);
      setGallery(response.data || []);
    } catch (error) {
      console.error('Gallery fetch error:', error);
      setUploadStatus('Failed to fetch gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLikeCountConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/like-count`);
      setShowLikeCount(response.data.showLikeCount === true);
    } catch (error) {
      console.error('Like count config fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
    fetchLikeCountConfig();
  }, [fetchGallery, fetchLikeCountConfig]);

  const handleRefreshGallery = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_BASE_URL}/api/gallery-list`);
      setGallery(response.data || []);
      enqueueSnackbar('✅ Gallery refreshed successfully', {
        variant: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Gallery refresh error:', error);
      enqueueSnackbar('❌ Failed to refresh gallery', {
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleLikeCount = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/config/like-count`,
        { showLikeCount: !showLikeCount }
      );
      setShowLikeCount(!showLikeCount);
    } catch (error) {
      console.error('Toggle like count error:', error);
    }
  };

  const convertToPng = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 2048,
      fileType: 'image/png',
      initialQuality: 0.8,
      alwaysKeepResolution: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return new File([compressedFile], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
    } catch (e) {
      throw new Error('Image conversion/compression failed: ' + e.message);
    }
  };

  const getPresignedUrl = async () => {
    const response = await axios.post(
      `${API_BASE_URL}/api/presign-gallery`,
      { fileExtension: 'png' }
    );
    return response.data.url;
  };

  const uploadToS3 = async (file, presignedUrl) => {
    await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': 'image/png' },
    });
    return presignedUrl.split('?')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageFile) {
      setUploadStatus('No image selected.');
      return;
    }

    setUploading(true);
    setUploadStatus('Uploading...');

    try {
      // Convert image to PNG
      const pngFile = await convertToPng(formData.imageFile);

      // Get presigned URL
      const presignedUrl = await getPresignedUrl();

      // Upload to S3
      const s3Url = await uploadToS3(pngFile, presignedUrl);

      // Save metadata to database
      await axios.post(
        `${API_BASE_URL}/api/gallery`,
        {
          image_url: s3Url,
          prompt: formData.prompt,
          aspect: formData.aspect,
          active: formData.active,
          pinned: formData.pinned,
        }
      );

      setUploadStatus('Upload successful!');
      enqueueSnackbar('✅ Image uploaded successfully!', {
        variant: 'success',
        autoHideDuration: 4000
      });

      setFormData(prev => ({
        ...prev,
        imageFile: null,
        prompt: '',
        pinned: false,
      }));

      await fetchGallery();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message;
      setUploadStatus('Upload failed: ' + errorMessage);
      enqueueSnackbar(`❌ Upload failed: ${errorMessage}`, {
        variant: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePin = async (id) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/gallery-pin`,
        { id }
      );

      const action = response.data.pinned ? 'pinned' : 'unpinned';
      enqueueSnackbar(`✅ Image ${action} successfully`, {
        variant: 'success',
        autoHideDuration: 3000
      });

      await fetchGallery();
    } catch (error) {
      console.error('Pin error:', error);
      enqueueSnackbar('❌ Failed to pin/unpin image', {
        variant: 'error',
        autoHideDuration: 4000
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/gallery-delete`, {
        data: { id: selectedImage.id, image_url: selectedImage.image_url },
      });

      enqueueSnackbar('✅ Image deleted successfully', {
        variant: 'success',
        autoHideDuration: 3000
      });

      setDeleteDialogOpen(false);
      setSelectedImage(null);
      await fetchGallery();
    } catch (error) {
      console.error('Delete error:', error);
      enqueueSnackbar('❌ Failed to delete image', {
        variant: 'error',
        autoHideDuration: 4000
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Gallery Management
      </Typography>

      {/* Upload Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add New Image
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData(prev => ({ ...prev, imageFile: e.target.files[0] }))}
                required
                style={{ width: '100%', padding: '8px' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prompt"
                value={formData.prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                step="0.1"
                label="Aspect Ratio"
                value={formData.aspect}
                onChange={(e) => setFormData(prev => ({ ...prev, aspect: parseFloat(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" flexDirection="column" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pinned}
                      onChange={(e) => setFormData(prev => ({ ...prev, pinned: e.target.checked }))}
                    />
                  }
                  label="Pinned"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Add />}
                disabled={uploading}
                size="large"
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {uploadStatus && (
          <Alert
            severity={uploadStatus.includes('successful') ? 'success' : uploadStatus.includes('failed') ? 'error' : 'info'}
            sx={{ mt: 2 }}
          >
            {uploadStatus}
          </Alert>
        )}
      </Paper>

      {/* Like Count Toggle */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          UI Settings
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showLikeCount}
              onChange={handleToggleLikeCount}
            />
          }
          label="Show Like Count in Flutter UI"
        />
        <Typography variant="body2" color="textSecondary">
          {showLikeCount ? 'Like counts will be visible to users' : 'Like counts will be hidden from users'}
        </Typography>
      </Paper>

      {/* Gallery Grid */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
        Gallery Images ({gallery.length})
      </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefreshGallery}
          disabled={refreshing}
          size="small"
        >
          {refreshing ? 'Refreshing...' : 'Refresh Gallery'}
        </Button>
      </Box>

      {gallery.length === 0 ? (
        <Alert severity="info">
          No images in gallery. Upload some images to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {gallery.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Card elevation={image.pinned ? 4 : 2} sx={{
                border: image.pinned ? '2px solid primary.main' : 'none',
                position: 'relative'
              }}>
                {image.pinned && (
                  <Chip
                    icon={<PushPin />}
                    label="Pinned"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 1
                    }}
                  />
                )}

                <CardMedia
                  component="img"
                  height="200"
                  image={image.image_url}
                  alt={image.prompt}
                  sx={{ objectFit: 'cover' }}
                />

                <CardContent>
                  <Typography variant="body2" color="text.primary" gutterBottom>
                    {image.prompt}
                  </Typography>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={`Aspect: ${image.aspect}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={image.active ? <Visibility /> : <VisibilityOff />}
                      label={image.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={image.active ? 'success' : 'default'}
                    />
                    {Array.isArray(image.likes) && (
                      <Chip
                        label={`${image.likes.length} likes`}
                        size="small"
                        color="info"
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  {!image.pinned ? (
                    <Button
                      size="small"
                      startIcon={<PushPin />}
                      onClick={() => handlePin(image.id)}
                    >
                      Pin
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<PushPin />}
                      onClick={() => handlePin(image.id)}
                      color="primary"
                    >
                      Unpin
                    </Button>
                  )}

                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={() => {
                      setSelectedImage(image);
                      setDeleteDialogOpen(true);
                    }}
                    color="error"
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
          {selectedImage && (
            <Box mt={2}>
              <img
                src={selectedImage.image_url}
                alt={selectedImage.prompt}
                style={{ width: '100%', maxWidth: '300px', borderRadius: '8px' }}
              />
              <Typography variant="body2" color="textSecondary" mt={1}>
                "{selectedImage.prompt}"
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GalleryManagement;
